import {
  CampaignDetailModelState,
  Condition,
  ContractDetailModel,
  CoolingOffCondition,
  CoolingOffDetails,
  CoolingOffType,
  Deposit,
  DocumentGroup,
  FormCondition,
  UploadedDocument,
  UploadedDocumentDocumentType,
  UploadedDocumentInfo,
  UploadedDocumentInfoDocumentType,
  ValueType,
  WaiverOfCoolingOff,
} from '@urbanx/agx.campaigns.client';
import { DocumentTypesMap } from '@urbanx/agx-ui-components';

export const REQUIRED_CONTRACT_DATE_STEP_NAME = 'Contract Date completed';
export const REQUIRED_SALE_PRICE_STEP_NAME = 'Sale Price is completed';
export const INITIAL_DEPOSIT_STEP_NAME = 'Initial Deposit is completed';
export const BALANCE_DEPOSIT_STEP_NAME = 'Balance Deposit addressed';
export const COOLING_OFF_PERIOD_STEP_NAME = 'Cooling off period completed';
export const FINANCE_CONDITION_STEP_NAME = 'Finance condition addressed';
export const BUILDING_AND_PEST_STEP_NAME = 'Building & Pest addressed';
export const BODY_CORPORATE_RECORDS_STEP_NAME = 'Body Corporate Records condition addressed';
export const SETTLEMENT_DATE_STEP_NAME = 'Settlement date is completed';

export const resolveCoolingOffDetailsForSave = (
  campaignState: CampaignDetailModelState | undefined,
  hasCoolingOffPeriodStep: boolean,
  currentCoolingOffDetails: CoolingOffDetails | undefined,
  waiverOfCoolingOff: WaiverOfCoolingOff | undefined
): CoolingOffDetails | undefined => {
  if (campaignState !== CampaignDetailModelState.QLD || !hasCoolingOffPeriodStep) {
    return currentCoolingOffDetails;
  }

  if (waiverOfCoolingOff?.coolingOffCondition === CoolingOffCondition.IncludeWaiverOfCoolingOff) {
    return { coolingOffType: CoolingOffType.NoCoolingOff };
  }

  if (
    waiverOfCoolingOff?.coolingOffCondition === CoolingOffCondition.IncludeShorteningOfCoolingOff
  ) {
    return {
      coolingOffType: CoolingOffType.NDays,
      coolingOffDays: waiverOfCoolingOff.shortenCoolingOffDays,
    };
  }

  return { coolingOffType: CoolingOffType.FiveDays };
};

const contractComplianceDocumentTypes = new Set<UploadedDocumentDocumentType>([
  UploadedDocumentDocumentType.SignedContract,
  UploadedDocumentDocumentType.WaiverOfCoolingOff,
  UploadedDocumentDocumentType.NoticeOfMultipleOffers,
  UploadedDocumentDocumentType.Miscellaneous,
  UploadedDocumentDocumentType.SalesContractAnnexure,
  UploadedDocumentDocumentType.AdditionalAnnexure,
  UploadedDocumentDocumentType.SalesAdvice,
]);

const getDocumentKey = (document: UploadedDocument) =>
  [
    document.containerName,
    document.documentType,
    document.containerFilePath,
    document.documentId,
    document.fileName,
  ]
    .filter(Boolean)
    .join('/');

const toUploadedDocumentInfo = (document: UploadedDocument): UploadedDocumentInfo => ({
  ...document,
  title: DocumentTypesMap[document.documentType as keyof typeof DocumentTypesMap],
  documentType: document.documentType as unknown as UploadedDocumentInfoDocumentType,
});

export const isSpecialConditionsComplianceStep = (stepName: string | undefined) => {
  const normalizedStepName = stepName?.toLowerCase() ?? '';

  return (
    normalizedStepName.includes('special conditions') &&
    (normalizedStepName.includes('addressed') || normalizedStepName.includes('completed'))
  );
};

export const isDepositComplete = (deposit: Deposit | undefined, requireSelection = true) => {
  if (!deposit) return false;
  if (requireSelection && !deposit.isDepositSelected) return false;
  if (!deposit.isDepositSelected) return true;

  const hasAmount =
    deposit.depositValueType === ValueType.Percentage
      ? deposit.percent != null && deposit.percent > 0
      : deposit.amount != null && deposit.amount > 0;

  if (!hasAmount || !deposit.condition) return false;

  if (deposit.condition === Condition.OnSpecificDate && !deposit.referenceDate) {
    return false;
  }

  if (deposit.condition === Condition.NdaysFromDate && deposit.daysFromDate == null) {
    return false;
  }

  if (
    deposit.condition === Condition.UponSatisfactionOfSpecialCondition &&
    !deposit.specialConditionId
  ) {
    return false;
  }

  return true;
};

export const hasDepositValidationError = (
  deposit: Deposit | undefined,
  requireSelection = true
) => {
  if (!deposit) return false;
  if (requireSelection && !deposit.isDepositSelected) return false;
  if (!deposit.isDepositSelected) return false;

  return !isDepositComplete(deposit, false);
};

export const isFinanceConditionComplete = (financeCondition: FormCondition | undefined) => {
  if (!financeCondition?.condition) return false;
  if (financeCondition.condition === Condition.NotSubjectToFinance) return true;

  if (financeCondition.condition === Condition.OnSpecificDate && !financeCondition.referenceDate) {
    return false;
  }

  if (
    financeCondition.condition === Condition.NdaysFromDate &&
    financeCondition.daysFromDate == null
  ) {
    return false;
  }

  if (
    financeCondition.condition === Condition.UponSatisfactionOfSpecialCondition &&
    !financeCondition.specialConditionId
  ) {
    return false;
  }

  return true;
};

export const hasFinanceConditionValidationError = (financeCondition: FormCondition | undefined) => {
  if (!financeCondition?.condition) return false;
  return !isFinanceConditionComplete(financeCondition);
};

export const isBuildingAndPestComplete = (buildingAndPestCondition: FormCondition | undefined) => {
  if (!buildingAndPestCondition?.condition) return false;
  if (buildingAndPestCondition.condition === Condition.NotApplicable) return true;

  if (
    buildingAndPestCondition.condition === Condition.OnSpecificDate &&
    !buildingAndPestCondition.referenceDate
  ) {
    return false;
  }

  if (
    buildingAndPestCondition.condition === Condition.NdaysFromDate &&
    buildingAndPestCondition.daysFromDate == null
  ) {
    return false;
  }

  if (
    buildingAndPestCondition.condition === Condition.UponSatisfactionOfSpecialCondition &&
    !buildingAndPestCondition.specialConditionId
  ) {
    return false;
  }

  return true;
};

export const hasBuildingAndPestValidationError = (
  buildingAndPestCondition: FormCondition | undefined
) => {
  if (!buildingAndPestCondition?.condition) return false;
  return !isBuildingAndPestComplete(buildingAndPestCondition);
};

export const isBodyCorporateRecordComplete = (
  bodyCorporateRecordsCondition: FormCondition | undefined
) => {
  if (!bodyCorporateRecordsCondition?.condition) return false;
  if (bodyCorporateRecordsCondition.condition === Condition.NotApplicable) {
    return true;
  }

  if (
    bodyCorporateRecordsCondition.condition === Condition.OnSpecificDate &&
    !bodyCorporateRecordsCondition.referenceDate
  ) {
    return false;
  }

  if (
    bodyCorporateRecordsCondition.condition === Condition.NdaysFromDate &&
    bodyCorporateRecordsCondition.daysFromDate == null
  ) {
    return false;
  }

  if (
    bodyCorporateRecordsCondition.condition === Condition.UponSatisfactionOfSpecialCondition &&
    !bodyCorporateRecordsCondition.specialConditionId
  ) {
    return false;
  }

  return true;
};

export const hasBodyCorporateRecordValidationError = (
  bodyCorporateRecordsCondition: FormCondition | undefined
) => {
  if (!bodyCorporateRecordsCondition?.condition) return false;
  return !isBodyCorporateRecordComplete(bodyCorporateRecordsCondition);
};

export const isSettlementDateComplete = (settlementInfo: FormCondition | undefined) => {
  if (!settlementInfo?.condition) return false;

  if (
    settlementInfo.condition === Condition.AsPerContract ||
    settlementInfo.condition === Condition.NotApplicable
  ) {
    return true;
  }

  if (settlementInfo.condition === Condition.NdaysFromDate && settlementInfo.daysFromDate == null) {
    return false;
  }

  if (settlementInfo.condition === Condition.OnSpecificDate && !settlementInfo.referenceDate) {
    return false;
  }

  if (
    settlementInfo.condition === Condition.UponSatisfactionOfSpecialCondition &&
    !settlementInfo.specialConditionId
  ) {
    return false;
  }

  if (settlementInfo.condition === Condition.Other && !settlementInfo.other) {
    return false;
  }

  return true;
};

export const hasSettlementDateValidationError = (settlementInfo: FormCondition | undefined) => {
  if (!settlementInfo?.condition) return false;
  return !isSettlementDateComplete(settlementInfo);
};

export const getContractComplianceDocumentGroups = (
  contract?: ContractDetailModel
): DocumentGroup[] => {
  const seen = new Set<string>();
  const documents = (contract?.documents ?? [])
    .filter(
      (document): document is UploadedDocument =>
        !!document?.containerFilePath &&
        !!document.documentType &&
        contractComplianceDocumentTypes.has(document.documentType)
    )
    .filter((document) => {
      const key = getDocumentKey(document);
      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    })
    .map(toUploadedDocumentInfo);

  return documents.length > 0
    ? [
        {
          title: contract?.contractName ?? 'Contract Documents',
          documents,
        },
      ]
    : [];
};
